import React, { useState } from 'react';
import theme from './FeatureInfo.module.scss';
import { Breadcrumbs, CustomLink, DateDisplay, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import PrimaryCard from '@/components/Statistics/PrimaryCard';
import { getFeatureDefinitions, setToggleForAll } from '@/services/featureDefinition';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import { useParams } from 'react-router';
import {
    FeatureToggleProfileDTO,
    FeatureToggleProjectDTO,
    searchProfileFeatures,
    searchProjectFeatures,
} from '@/services/featureToggle';
import { createDataTable } from '@/components/DataTable';
import { SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';

export interface FeatureInfoProps {}

const ProfileDataTable = createDataTable<FeatureToggleProfileDTO>();
const ProjectDataTable = createDataTable<FeatureToggleProjectDTO>();
type tabIds = 'profile' | 'project';

export const FeatureInfo: React.FC<FeatureInfoProps> = () => {
    const { feature } = useParams<{ feature: string }>();
    const [featureDefinitions, , reloadData] = useAsyncStateWithIsLoading(getFeatureDefinitions, [], null);
    const [tabId, setTabId] = useState<tabIds>('profile');
    const [profileTableState, setProfileTableState] = useState(ProfileDataTable.getInitialState('featureName'));
    const [projectTableState, setProjectTableState] = useState(ProjectDataTable.getInitialState('featureName'));

    const [profileFeatures, , ,] = useAsyncStateWithIsLoading(
        () => searchProfileFeatures({ ...profileTableState, feature: feature, pageSize: 20 }),
        [profileTableState],
        null
    );

    const [projectFeatures, , ,] = useAsyncStateWithIsLoading(
        () => searchProjectFeatures({ ...projectTableState, feature: feature, pageSize: 20 }),
        [projectTableState],
        null
    );

    const data = featureDefinitions?.find((x) => x.featureName === feature);

    const handleToggleForAll = async (toggle: boolean | null) => {
        if (toggle === true) {
            if (
                !confirm(
                    'Warning:\nYou are about to enable this feature for all companies and projects.\nThis action will make the feature active everywhere.'
                )
            ) {
                return;
            }
        } else {
            if (
                !confirm(
                    'Disabling “Enable for all” will remove access to this feature for all companies and projects, except those that have manually enabled it in their company or project settings.'
                )
            ) {
                return;
            }
        }
        await setToggleForAll(data!.featureName, toggle);
        reloadData();
    };
    if (!profileFeatures || !featureDefinitions || !projectFeatures || !data) return <LoadingIndicator />;
    return (
        <>
            <Breadcrumbs>
                <Breadcrumbs.Link to="/features">Features</Breadcrumbs.Link>
                <Breadcrumbs.Item>{data?.featureName}</Breadcrumbs.Item>
            </Breadcrumbs>
            <h2>{data?.featureName}</h2>
            <div className={theme.grid}>
                <PrimaryCard className={theme.infoContainer} title={''}>
                    {() => (
                        <>
                            {/* <PrimaryCard.Row label="Name">{data?.featureName}</PrimaryCard.Row> */}
                            <PrimaryCard.Row label="information">{data?.information}</PrimaryCard.Row>
                            <PrimaryCard.Row label="isExperimental">{data?.isExperimental ? 'Yes' : 'No'}</PrimaryCard.Row>
                            <PrimaryCard.Row label="isPaid">{data?.isPaid ? 'Yes' : 'No'}</PrimaryCard.Row>
                            <PrimaryCard.Row label="isProfileOnlyFeature">
                                {data?.isProfileOnlyFeature ? 'Yes' : 'No'}
                            </PrimaryCard.Row>
                            <PrimaryCard.Row label="Number of profiles with feature">
                                {profileFeatures.totalCount}
                            </PrimaryCard.Row>
                            <PrimaryCard.Row label="Number of projects with feature">
                                {projectFeatures.totalCount}
                            </PrimaryCard.Row>
                            <PrimaryCard.Row label="ENABLE for all">
                                <SlideToggle
                                    checked={data.toggleForAll === true}
                                    onChange={(e) => handleToggleForAll(e.target.checked ? true : null)}>
                                    Enable for all
                                </SlideToggle>
                            </PrimaryCard.Row>
                            <PrimaryCard.Row label="DISABLE for all">
                                <SlideToggle
                                    checked={data.toggleForAll === false}
                                    onChange={(e) => handleToggleForAll(e.target.checked ? false : null)}>
                                    Disable for all
                                </SlideToggle>
                            </PrimaryCard.Row>
                        </>
                    )}
                </PrimaryCard>
                <div>
                    <Tabs activeId={tabId} onChange={(id) => setTabId(id as tabIds)} variant="underline">
                        <Tabs.Tab id="profile" label="Profile" />
                        <Tabs.Tab id="project" label="Project" />
                    </Tabs>
                    <div className="tab-panel">
                        {tabId === 'profile' && (
                            <ProfileDataTable
                                totalItemCount={profileFeatures.totalCount}
                                onStateChange={setProfileTableState}
                                state={profileTableState}
                                hasPagination={true}
                                className="table style-3"
                                data={profileFeatures.items}>
                                <ProfileDataTable.Column sortable header="Profile Id" field="profileId" />
                                <ProfileDataTable.Column
                                    sortable
                                    header="Profile "
                                    field="profileName"
                                    renderer={({ row, value }) => (
                                        <CustomLink to={`/profiles/${row.profileId}`}> {value}</CustomLink>
                                    )}
                                />
                                <ProfileDataTable.Column sortable header="Created By" field="createdByEmail" />
                                <ProfileDataTable.Column sortable header="Updated By" field="updatedByEmail" />
                                <ProfileDataTable.Column
                                    sortable
                                    header="Create date"
                                    field="createDate"
                                    renderer={({ value }) => <DateDisplay value={value} />}
                                />
                                <ProfileDataTable.Column
                                    sortable
                                    header="Update date"
                                    field="updateDate"
                                    renderer={({ value }) => <DateDisplay value={value} />}
                                />
                            </ProfileDataTable>
                        )}
                        {tabId === 'project' && (
                            <ProjectDataTable
                                totalItemCount={projectFeatures.totalCount}
                                onStateChange={setProjectTableState}
                                state={projectTableState}
                                hasPagination={true}
                                className="table style-3"
                                data={projectFeatures.items}>
                                <ProjectDataTable.Column header="Project Id" field="projectId" />
                                <ProjectDataTable.Column
                                    sortable
                                    header="Project "
                                    field="projectName"
                                    renderer={({ row, value }) => (
                                        <CustomLink to={`/projects/${row.projectId}`}> {value}</CustomLink>
                                    )}
                                />
                                <ProjectDataTable.Column
                                    sortable
                                    header="Toggled by profile"
                                    field="profileToggled"
                                    renderer={(x) => (x.value ? 'Yes' : 'No')}
                                />
                                <ProjectDataTable.Column sortable header="Created By" field="createdByEmail" />
                                <ProjectDataTable.Column sortable header="Updated By" field="updatedByEmail" />
                                <ProjectDataTable.Column
                                    sortable
                                    header="Create date"
                                    field="createDate"
                                    renderer={({ value }) => <DateDisplay value={value} />}
                                />
                                <ProjectDataTable.Column
                                    sortable
                                    header="Update date"
                                    field="updateDate"
                                    renderer={({ value }) => <DateDisplay value={value} />}
                                />
                            </ProjectDataTable>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
